import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SlidersHorizontal, X } from "lucide-react";
import { Button } from "./ui/button";

interface PageFilterPopoverProps {
  count?: number;
  onClear?: () => void;
  children: ReactNode;
  label?: string;
}

/**
 * Filter button + popover (desktop, ≥lg) or bottom sheet (mobile, <lg).
 * Used in the topbar slot via usePageHeader to keep filter UI off the page body.
 */
export function PageFilterPopover({
  count = 0,
  onClear,
  children,
  label,
}: PageFilterPopoverProps) {
  const { t } = useTranslation("clients");
  const resolvedLabel = label ?? t("filter.title");
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <SlidersHorizontal />
        <span className="hidden sm:inline">{resolvedLabel}</span>
        {count > 0 && (
          <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
            {count}
          </span>
        )}
      </Button>

      {open && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/50 animate-fade-in"
            onClick={() => setOpen(false)}
          />

          <div
            className="
              lg:hidden fixed inset-x-0 bottom-0 z-50 max-h-[80vh]
              bg-card text-card-foreground border-t rounded-t-xl shadow-xl
              flex flex-col animate-fade-in-up
            "
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-sm font-semibold">{resolvedLabel}</h3>
              <button
                onClick={() => setOpen(false)}
                className="rounded-md p-1.5 hover:bg-muted text-muted-foreground"
                aria-label={t("filter.close")}
              >
                <X className="size-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">{children}</div>
            {onClear && (
              <div className="p-3 border-t bg-muted/30">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    onClear();
                    setOpen(false);
                  }}
                >
                  {t("filter.reset")}
                </Button>
              </div>
            )}
          </div>

          <div
            className="
              hidden lg:flex absolute right-0 top-full mt-2 z-50
              w-80 max-w-[calc(100vw-2rem)]
              bg-card text-card-foreground border rounded-lg shadow-lg
              flex-col animate-fade-in
            "
          >
            <div className="p-4 space-y-3">{children}</div>
            {onClear && (
              <div className="p-3 border-t bg-muted/30 rounded-b-lg">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={onClear}
                >
                  {t("filter.reset")}
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
